import type { Rule } from './types';

const RECOGNIZED_SUBDIRS = new Set(['scripts', 'references', 'assets']);

export const structureRules: Rule[] = [
  {
    id: 'structure.frontmatter-delimiters',
    category: 'structure',
    severity: 'fail',
    weight: 8,
    title: 'Frontmatter delimiters present',
    appliesTo: 'both',
    run(ctx) {
      const ok = !ctx.parsed.parseError ||
        !ctx.parsed.parseError.startsWith('No frontmatter delimiters');
      const hasClose = !ctx.parsed.parseError?.startsWith('Frontmatter is missing');
      if (!ok || !hasClose) {
        return {
          status: 'fail',
          message: ctx.parsed.parseError ?? 'Missing frontmatter delimiters.',
          fix: 'Wrap your YAML frontmatter with `---` on its own line before and after.',
        };
      }
      return { status: 'pass', message: 'Opens and closes with `---`.' };
    },
  },
  {
    id: 'structure.frontmatter-parses',
    category: 'structure',
    severity: 'fail',
    weight: 8,
    title: 'Frontmatter is valid YAML',
    appliesTo: 'both',
    run(ctx) {
      if (Object.keys(ctx.parsed.frontmatter).length === 0) {
        return {
          status: 'fail',
          message: ctx.parsed.parseError ?? 'No frontmatter found.',
          fix: 'Add YAML frontmatter with at least `name` and `description` fields.',
        };
      }
      if (ctx.parsed.parseError && !ctx.parsed.parseError.startsWith('No frontmatter')) {
        return {
          status: 'fail',
          message: `YAML failed to parse: ${ctx.parsed.parseError}`,
          fix: 'Check indentation, unclosed quotes, and stray colons in the frontmatter block.',
        };
      }
      return { status: 'pass', message: 'Parsed cleanly.' };
    },
  },
  {
    id: 'structure.has-body',
    category: 'structure',
    severity: 'warn',
    weight: 4,
    title: 'Has a markdown body',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.parsed.body.length < 20) {
        return {
          status: 'warn',
          message: 'The instruction body is empty or extremely short.',
          fix: 'Add an Instructions section with concrete steps Claude should follow.',
        };
      }
      return { status: 'pass', message: 'Body present.' };
    },
  },
  {
    id: 'structure.skillmd-filename',
    category: 'structure',
    severity: 'fail',
    weight: 5,
    title: 'File is named exactly `SKILL.md`',
    appliesTo: 'github',
    run(ctx) {
      if (!ctx.skillFilename) {
        return {
          status: 'fail',
          message: 'No `SKILL.md` found in the target folder.',
          fix: 'Add a file named exactly `SKILL.md` (case-sensitive) at the skill folder root.',
        };
      }
      if (ctx.skillFilename !== 'SKILL.md') {
        return {
          status: 'fail',
          message: `Found \`${ctx.skillFilename}\`. Must be exactly \`SKILL.md\` (case-sensitive).`,
          fix: 'Rename the file to `SKILL.md`.',
        };
      }
      return { status: 'pass', message: 'Filename is correct.' };
    },
  },
  {
    id: 'structure.no-readme-in-folder',
    category: 'structure',
    severity: 'warn',
    weight: 3,
    title: 'No README.md inside the skill folder',
    appliesTo: 'github',
    run(ctx) {
      const hasReadme = (ctx.directoryListing ?? []).some(
        (entry) => entry.toLowerCase() === 'readme.md',
      );
      if (hasReadme) {
        return {
          status: 'warn',
          message: 'A `README.md` was found inside the skill folder.',
          fix: 'Move human-facing docs to the repo root README; keep skill-internal docs in `references/`.',
        };
      }
      return { status: 'pass', message: 'No README.md inside the folder.' };
    },
  },
  {
    id: 'structure.subdirs-recognized',
    category: 'structure',
    severity: 'info',
    weight: 2,
    title: 'Subdirectories match conventions',
    appliesTo: 'github',
    run(ctx) {
      const dirs = (ctx.directoryListing ?? []).filter((e) => e.endsWith('/'));
      const unknown = dirs
        .map((d) => d.replace(/\/$/, ''))
        .filter((d) => !RECOGNIZED_SUBDIRS.has(d));
      if (unknown.length > 0) {
        return {
          status: 'warn',
          message: `Non-standard subdirectories: ${unknown.map((d) => `\`${d}/\``).join(', ')}.`,
          fix: 'Conventional folders are `scripts/`, `references/`, and `assets/`.',
        };
      }
      return { status: 'pass', message: 'All subdirectories are conventional.' };
    },
  },
];
